import { useInfiniteQuery } from '@tanstack/react-query';
import { isSameDay } from 'date-fns';
import { Loader2Icon, NewspaperIcon, SearchIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { apiChangelogsGetCollection } from '@r/api/generated/client.ts';
import type { ChangelogChangelogReadModelTag } from '@r/api/generated/client.schemas.ts';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { toUtcEndOfDay, toUtcStartOfDay } from '@r/utils/date-utils.ts';
import { DatePicker } from '@ui/date-picker.tsx';
import { Input } from '@ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { Timeline } from '@ui/timeline.tsx';

import { ChangelogTimelineItem } from './changelog-timeline-item.tsx';

const PER_PAGE = 10;
/** Marge de préchargement, uniquement sous le viewport : la sentinelle déclenche 600px avant d'entrer en vue. */
const PRELOAD_MARGIN = '0px 0px 600px 0px';
const TAG_OPTIONS: ChangelogChangelogReadModelTag[] = ['feature', 'improvement', 'bugfix'];
const ALL_TYPES = 'all';

/**
 * Flux chronologique des nouveautés : recherche par titre, filtre par type et par date,
 * affichage groupé par jour et chargement au scroll (scroll infini).
 */
export function ChangelogFeed() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string>(ALL_TYPES);
  const [date, setDate] = useState<Date | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 400);

  const query = useInfiniteQuery({
    queryKey: [
      'changelogs-feed',
      { search: debouncedSearch, tag, date: date?.toISOString() ?? null }
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiChangelogsGetCollection({
        page: pageParam,
        perPage: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(tag !== ALL_TYPES ? { tag } : {}),
        ...(date
          ? {
              'createdAt[after]': toUtcStartOfDay(date).toISOString(),
              'createdAt[before]': toUtcEndOfDay(date).toISOString()
            }
          : {})
      }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.pagination;
      if (!pagination) return undefined;
      return pagination.current < pagination.last ? pagination.current + 1 : undefined;
    }
  });

  useOrvalQueryErrorToast(
    { isError: query.isError, error: query.error, errorUpdatedAt: query.errorUpdatedAt },
    t('changelog.feed.error')
  );

  // Dédoublonnage par id : un même changelog peut apparaître sur deux pages
  // consécutives quand plusieurs entrées partagent le même `createdAt` (le tri
  // n'est alors pas stable entre deux requêtes paginées côté back).
  const items = useMemo(() => {
    const all = query.data?.pages.flatMap((page) => page.data ?? []) ?? [];
    const seen = new Set<number>();
    return all.filter((changelog) => {
      if (seen.has(changelog.id)) return false;
      seen.add(changelog.id);
      return true;
    });
  }, [query.data]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  // Scroll infini (pattern officiel TanStack Query) : `fetchNextPage` sur chaque
  // transition d'intersection de la sentinelle. L'effet ne dépend QUE de `inView` :
  // pas de `isFetchingNextPage` dans les deps, sinon il se rejoue pendant le fetch
  // avec un `inView` encore périmé et déclenche une page en trop. `fetchNextPage`
  // est no-op s'il n'y a plus de page ou si un fetch est déjà en cours.
  const { ref: sentinelRef, inView } = useInView({ rootMargin: PRELOAD_MARGIN });

  useEffect(() => {
    if (inView) fetchNextPage();
  }, [inView, fetchNextPage]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center">
        <div className="relative min-w-0 sm:flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="border-none pl-9 shadow-none focus-visible:ring-0"
            placeholder={t('changelog.feed.search.placeholder')}
          />
        </div>

        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="sm:w-48 sm:shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES}>{t('changelog.feed.filter.type.all')}</SelectItem>
            {TAG_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`changelog.tag.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DatePicker
          value={date}
          onChange={setDate}
          placeholder={t('changelog.feed.filter.date.placeholder')}
          formatStr="d MMM yyyy"
          className="sm:w-48 sm:shrink-0"
        />
      </div>

      <div className="mx-auto w-full max-w-4xl">
        {query.isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex gap-4">
                <Skeleton className="h-4 w-20 shrink-0" />
                <Skeleton className="h-32 flex-1 rounded-xl" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border py-16 text-muted-foreground">
            <NewspaperIcon className="size-12" />
            <p className="font-medium">{t('changelog.feed.empty')}</p>
          </div>
        ) : (
          <Timeline>
            {items.map((changelog, index) => {
              const previous = items[index - 1];
              const showDate =
                !previous ||
                !changelog.createdAt ||
                !previous.createdAt ||
                !isSameDay(new Date(changelog.createdAt), new Date(previous.createdAt));

              return (
                <ChangelogTimelineItem
                  key={changelog.id}
                  changelog={changelog}
                  showDate={showDate}
                  isLast={index === items.length - 1 && !hasNextPage}
                />
              );
            })}
          </Timeline>
        )}

        {/* Montée seulement quand il reste des pages : absente pendant le skeleton,
            donc `inView` démarre à false et ne déclenche pas de fetch au mount. */}
        {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
      </div>

      <div role="status" aria-live="polite" className="py-4 text-center text-sm">
        {isFetchingNextPage ? (
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" />
            {t('changelog.feed.loading.more')}
          </span>
        ) : (
          !query.isLoading &&
          items.length > 0 &&
          !hasNextPage && <span className="text-muted-foreground">{t('changelog.feed.end')}</span>
        )}
      </div>
    </div>
  );
}
